import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Trash2, Loader2, GripVertical, Image, Users, 
  X, Check, Edit2, FolderPlus, AlertCircle, UserPlus, Mail
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

/**
 * Extract character name from filename
 * "Dean profile.png" -> "Dean"
 * "Dean_front.png" -> "Dean"
 * "Dean-side.png" -> "Dean"
 * "sarah_pose2.png" -> "Sarah"
 */
const extractCharacterName = (filename) => {
  // Remove extension
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
  
  // Split by common separators: space, underscore, dash
  const parts = nameWithoutExt.split(/[\s_-]+/);
  
  // Take the first part as character name
  let name = parts[0] || nameWithoutExt;
  
  // Capitalize first letter
  name = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  
  return name;
};

/**
 * Group files by extracted character name
 */
const groupFilesByCharacter = (files) => {
  const groups = {};
  
  files.forEach((file, index) => {
    const name = extractCharacterName(file.name);
    if (!groups[name]) {
      groups[name] = [];
    }
    groups[name].push({
      id: `file-${index}-${Date.now()}`,
      file,
      preview: URL.createObjectURL(file),
      originalName: file.name
    });
  });
  
  return groups;
};

/**
 * Smart Import Modal for character images
 * Groups uploaded files by detected character names and allows reorganization
 * Now supports actor assignment during import
 */
export default function SmartImportModal({ 
  open, 
  onClose, 
  files, 
  existingCharacters = [],
  existingActors = [],
  onConfirm,
  onInviteActor,
  projectId,
  uploadProgress = null // { current, total, currentFile }
}) {
  // State for character groups: { characterName: [{ id, file, preview }] }
  const [groups, setGroups] = useState({});
  // Track which groups are existing characters vs new
  const [existingMatches, setExistingMatches] = useState({}); // { groupName: characterProfile }
  // Track actor assignments per character group
  const [actorAssignments, setActorAssignments] = useState({}); // { groupName: actorId }
  // Track new actor invites per group
  const [newActorInvites, setNewActorInvites] = useState({}); // { groupName: email }
  const [invitingActorFor, setInvitingActorFor] = useState(null); // groupName being invited to
  const [inviteEmail, setInviteEmail] = useState("");
  // Editing group name
  const [editingGroup, setEditingGroup] = useState(null);
  const [editName, setEditName] = useState("");
  // Drag state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);
  // Loading
  const [importing, setImporting] = useState(false);

  // Initialize groups when files change
  useEffect(() => {
    if (files && files.length > 0) {
      const initialGroups = groupFilesByCharacter(files);
      setGroups(initialGroups);
      
      // Check for existing character matches
      const matches = {};
      const sortedExisting = [...existingCharacters].sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
      
      Object.keys(initialGroups).forEach(groupName => {
        const match = sortedExisting.find(
          char => char.name.toLowerCase() === groupName.toLowerCase()
        );
        if (match) {
          matches[groupName] = match;
        }
      });
      setExistingMatches(matches);
    }
  }, [files, existingCharacters]);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      Object.values(groups).flat().forEach(item => {
        if (item.preview) URL.revokeObjectURL(item.preview);
      });
    };
  }, []);

  // Get sorted group names
  const sortedGroupNames = Object.keys(groups).sort((a, b) => 
    a.toLowerCase().localeCompare(b.toLowerCase())
  );

  // Handle drag start
  const handleDragStart = (e, item, sourceGroup) => {
    setDraggedItem({ item, sourceGroup });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.id);
  };

  // Handle drag over
  const handleDragOver = (e, groupName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverGroup(groupName);
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverGroup(null);
  };

  // Handle drop
  const handleDrop = (e, targetGroup) => {
    e.preventDefault();
    setDragOverGroup(null);
    
    if (!draggedItem || draggedItem.sourceGroup === targetGroup) {
      setDraggedItem(null);
      return;
    }
    
    const { item, sourceGroup } = draggedItem;
    
    setGroups(prev => {
      const newGroups = { ...prev };
      
      // Remove from source
      newGroups[sourceGroup] = newGroups[sourceGroup].filter(i => i.id !== item.id);
      
      // Remove empty group
      if (newGroups[sourceGroup].length === 0) {
        delete newGroups[sourceGroup];
        // Also remove from existingMatches
        setExistingMatches(prev => {
          const updated = { ...prev };
          delete updated[sourceGroup];
          return updated;
        });
      }
      
      // Add to target
      if (!newGroups[targetGroup]) {
        newGroups[targetGroup] = [];
      }
      newGroups[targetGroup].push(item);
      
      return newGroups;
    });
    
    setDraggedItem(null);
  };

  // Handle creating new character group
  const handleCreateNewGroup = () => {
    const baseName = "New Character";
    let name = baseName;
    let counter = 1;
    
    while (groups[name]) {
      name = `${baseName} ${counter}`;
      counter++;
    }
    
    setGroups(prev => ({
      ...prev,
      [name]: []
    }));
    
    // Start editing the name
    setEditingGroup(name);
    setEditName(name);
  };

  // Handle renaming group
  const handleRenameGroup = (oldName) => {
    if (!editName.trim() || editName === oldName) {
      setEditingGroup(null);
      return;
    }
    
    // Check if name already exists
    if (groups[editName] && editName !== oldName) {
      // Merge into existing group
      setGroups(prev => {
        const newGroups = { ...prev };
        newGroups[editName] = [...(newGroups[editName] || []), ...newGroups[oldName]];
        delete newGroups[oldName];
        return newGroups;
      });
    } else {
      // Rename
      setGroups(prev => {
        const newGroups = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (key === oldName) {
            newGroups[editName] = value;
          } else {
            newGroups[key] = value;
          }
        });
        return newGroups;
      });
      
      // Update existing matches
      if (existingMatches[oldName]) {
        setExistingMatches(prev => {
          const updated = { ...prev };
          updated[editName] = updated[oldName];
          delete updated[oldName];
          return updated;
        });
      }
    }
    
    // Check for new existing character match
    const match = existingCharacters.find(
      char => char.name.toLowerCase() === editName.toLowerCase()
    );
    if (match) {
      setExistingMatches(prev => ({
        ...prev,
        [editName]: match
      }));
    }
    
    setEditingGroup(null);
  };

  // Handle removing image from group
  const handleRemoveImage = (groupName, itemId) => {
    setGroups(prev => {
      const newGroups = { ...prev };
      newGroups[groupName] = newGroups[groupName].filter(i => i.id !== itemId);
      
      // Remove empty group
      if (newGroups[groupName].length === 0) {
        delete newGroups[groupName];
        setExistingMatches(prevMatches => {
          const updated = { ...prevMatches };
          delete updated[groupName];
          return updated;
        });
      }
      
      return newGroups;
    });
  };

  // Handle removing entire group
  const handleRemoveGroup = (groupName) => {
    setGroups(prev => {
      const newGroups = { ...prev };
      delete newGroups[groupName];
      return newGroups;
    });
    setExistingMatches(prev => {
      const updated = { ...prev };
      delete updated[groupName];
      return updated;
    });
  };

  // Handle confirm import
  const handleConfirm = async () => {
    setImporting(true);
    
    try {
      // Build import data with actor assignments
      const importData = sortedGroupNames.map(groupName => ({
        name: groupName,
        existingCharacterId: existingMatches[groupName]?.id || null,
        files: groups[groupName].map(item => item.file),
        assignedActorId: actorAssignments[groupName] || null,
        inviteActorEmail: newActorInvites[groupName] || null
      })).filter(group => group.files.length > 0);
      
      await onConfirm(importData);
      onClose();
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setImporting(false);
    }
  };

  // Handle actor selection for a group
  const handleActorSelect = (groupName, actorId) => {
    setActorAssignments(prev => ({
      ...prev,
      [groupName]: actorId === "none" ? null : actorId
    }));
    // Clear any pending invite if selecting existing actor
    if (actorId && actorId !== "none" && actorId !== "invite") {
      setNewActorInvites(prev => {
        const updated = { ...prev };
        delete updated[groupName];
        return updated;
      });
    }
  };

  // Handle inviting a new actor
  const handleInviteSubmit = async (groupName) => {
    if (!inviteEmail || !inviteEmail.includes("@")) return;
    
    // Store the invite email
    setNewActorInvites(prev => ({
      ...prev,
      [groupName]: inviteEmail
    }));
    setInvitingActorFor(null);
    setInviteEmail("");
    
    // Clear actor selection for this group
    setActorAssignments(prev => {
      const updated = { ...prev };
      delete updated[groupName];
      return updated;
    });
  };

  // Count total images
  const totalImages = Object.values(groups).reduce((sum, items) => sum + items.length, 0);
  const totalCharacters = sortedGroupNames.filter(name => groups[name]?.length > 0).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />
            Smart Character Import
          </DialogTitle>
          <DialogDescription>
            We detected {totalCharacters} character{totalCharacters !== 1 ? 's' : ''} from {totalImages} image{totalImages !== 1 ? 's' : ''}. 
            Drag images between characters to reorganize.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto min-h-0 pr-2 -mr-2">
          <div className="space-y-4 py-2">
            {sortedGroupNames.map(groupName => {
              const items = groups[groupName] || [];
              const existingChar = existingMatches[groupName];
              const isEditing = editingGroup === groupName;
              const isDragOver = dragOverGroup === groupName;
              
              return (
                <div
                  key={groupName}
                  className={`
                    rounded-lg border-2 transition-all
                    ${isDragOver 
                      ? 'border-blue-500 bg-blue-500/10' 
                      : existingChar 
                        ? 'border-green-600/50 bg-green-900/10'
                        : 'border-zinc-700 bg-zinc-800/30'
                    }
                  `}
                  onDragOver={(e) => handleDragOver(e, groupName)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, groupName)}
                  data-testid={`character-group-${groupName}`}
                >
                  {/* Group Header */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700/50">
                    <div className="flex items-center gap-2 flex-1">
                      {isEditing ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameGroup(groupName);
                              if (e.key === 'Escape') setEditingGroup(null);
                            }}
                            className="h-7 bg-zinc-800 border-zinc-600 text-sm"
                            autoFocus
                            data-testid={`edit-group-name-${groupName}`}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-green-500 hover:text-green-400"
                            onClick={() => handleRenameGroup(groupName)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-zinc-400"
                            onClick={() => setEditingGroup(null)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className="font-medium">{groupName}</span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-zinc-500 hover:text-zinc-300"
                            onClick={() => {
                              setEditingGroup(groupName);
                              setEditName(groupName);
                            }}
                            data-testid={`edit-name-${groupName}`}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          {existingChar && (
                            <Badge className="bg-green-600/20 text-green-400 border-green-600/50 text-xs">
                              Add to existing
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-zinc-500 mr-2">
                        {items.length} image{items.length !== 1 ? 's' : ''}
                      </span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => handleRemoveGroup(groupName)}
                        title="Remove all images in this group"
                        data-testid={`remove-group-${groupName}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Images Grid */}
                  <div className="p-3">
                    {items.length === 0 ? (
                      <div className="text-center py-4 text-zinc-500 text-sm border-2 border-dashed border-zinc-700 rounded-lg">
                        <Image className="w-6 h-6 mx-auto mb-1 opacity-50" />
                        Drag images here
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {items.map(item => (
                          <div
                            key={item.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, item, groupName)}
                            className={`
                              relative group w-20 h-20 rounded-lg overflow-hidden border-2 
                              cursor-grab active:cursor-grabbing transition-all
                              ${draggedItem?.item.id === item.id 
                                ? 'opacity-50 border-blue-500' 
                                : 'border-zinc-600 hover:border-zinc-500'
                              }
                            `}
                            title={item.originalName}
                            data-testid={`image-${item.id}`}
                          >
                            <img
                              src={item.preview}
                              alt={item.originalName}
                              className="w-full h-full object-cover"
                              draggable={false}
                            />
                            
                            {/* Drag handle overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <GripVertical className="w-5 h-5 text-white opacity-0 group-hover:opacity-70" />
                            </div>
                            
                            {/* Remove button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage(groupName, item.id);
                              }}
                              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              title="Remove image"
                              data-testid={`remove-image-${item.id}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                            
                            {/* Filename tooltip */}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5 text-xs text-zinc-300 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                              {item.originalName}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Actor Assignment Section */}
                    {items.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-zinc-700/50">
                        <div className="flex items-center gap-2">
                          <UserPlus className="w-4 h-4 text-purple-400" />
                          <span className="text-xs text-zinc-400">Assign Actor:</span>
                          
                          {invitingActorFor === groupName ? (
                            <div className="flex items-center gap-2 flex-1">
                              <Input
                                type="email"
                                placeholder="Enter email to invite..."
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                className="h-7 bg-zinc-800 border-zinc-600 text-sm flex-1"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleInviteSubmit(groupName);
                                  if (e.key === 'Escape') {
                                    setInvitingActorFor(null);
                                    setInviteEmail("");
                                  }
                                }}
                                data-testid={`invite-email-${groupName}`}
                              />
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-green-500 hover:text-green-400"
                                onClick={() => handleInviteSubmit(groupName)}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-zinc-400"
                                onClick={() => {
                                  setInvitingActorFor(null);
                                  setInviteEmail("");
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : newActorInvites[groupName] ? (
                            <div className="flex items-center gap-2 flex-1">
                              <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/50 text-xs flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                Will invite: {newActorInvites[groupName]}
                              </Badge>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6 text-zinc-400 hover:text-red-400"
                                onClick={() => {
                                  setNewActorInvites(prev => {
                                    const updated = { ...prev };
                                    delete updated[groupName];
                                    return updated;
                                  });
                                }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <Select 
                              value={actorAssignments[groupName] || "none"}
                              onValueChange={(value) => {
                                if (value === "invite") {
                                  setInvitingActorFor(groupName);
                                  setInviteEmail("");
                                } else {
                                  handleActorSelect(groupName, value);
                                }
                              }}
                            >
                              <SelectTrigger className="h-7 w-48 bg-zinc-800 border-zinc-600 text-sm" data-testid={`actor-select-${groupName}`}>
                                <SelectValue placeholder="Select actor..." />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700">
                                <SelectItem value="none" className="text-zinc-400">No actor assigned</SelectItem>
                                {existingActors.map(actor => (
                                  <SelectItem key={actor.id} value={actor.id}>
                                    {actor.name || actor.email}
                                  </SelectItem>
                                ))}
                                <SelectItem value="invite" className="text-purple-400">
                                  <span className="flex items-center gap-1">
                                    <UserPlus className="w-3 h-3" />
                                    Invite new actor...
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Add New Character Button */}
            <button
              onClick={handleCreateNewGroup}
              className="w-full py-4 border-2 border-dashed border-zinc-700 rounded-lg text-zinc-500 hover:text-zinc-300 hover:border-zinc-500 transition-colors flex items-center justify-center gap-2"
              data-testid="create-new-character-group"
            >
              <FolderPlus className="w-5 h-5" />
              Create New Character
            </button>
          </div>
        </div>
        
        {/* Warning if no images */}
        {totalImages === 0 && (
          <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            All images have been removed. Add at least one image to import.
          </div>
        )}
        
        {/* Upload Progress Bar */}
        {uploadProgress && uploadProgress.total > 0 && (
          <div className="flex-shrink-0 space-y-2 px-1" data-testid="upload-progress-container">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">
                Uploading: {uploadProgress.currentFile || 'Processing...'}
              </span>
              <span className="text-blue-400 font-medium">
                {uploadProgress.current} / {uploadProgress.total}
              </span>
            </div>
            <Progress 
              value={(uploadProgress.current / uploadProgress.total) * 100} 
              className="h-2"
              data-testid="upload-progress-bar"
            />
          </div>
        )}
        
        <DialogFooter className="flex-shrink-0 mt-4">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-zinc-400"
            disabled={importing || (uploadProgress && uploadProgress.current > 0)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={importing || totalImages === 0 || (uploadProgress && uploadProgress.current > 0)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="confirm-smart-import"
          >
            {importing || (uploadProgress && uploadProgress.current > 0) ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Create {totalCharacters} Character{totalCharacters !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
